import {
  AccountingFilters,
  AccountingObject,
  EventAccounting,
  InvoiceObject,
  Paginated,
  PaginatedResponse,
  PurchaseObject,
  Sortable,
} from "@ac-expo/api-types";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { accountingQueryKeys, ClientsFilter } from "../query-keys";

const getEventsAccounting = async (filters: ClientsFilter) => {
  try {
    const { data } = await privateAxios.get<PaginatedResponse<EventAccounting>>(
      `/events/accounting`,
      { params: filters }
    );

    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetEventsAccounting = (filters: ClientsFilter) => {
  return useQuery({
    queryKey: accountingQueryKeys.clients(filters),
    queryFn: () => getEventsAccounting(filters),
  });
};

const getPrestationsAccounting = async (
  filters: Sortable<Paginated<AccountingFilters>>
) => {
  try {
    const { data } = await privateAxios.get<
      PaginatedResponse<AccountingObject>
    >(`/accounting/prestations`, { params: filters });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetPrestationsAccounting = (
  filters: Sortable<Paginated<AccountingFilters>>
) => {
  return useQuery({
    queryKey: accountingQueryKeys.prestations(filters),
    queryFn: () => getPrestationsAccounting(filters),
  });
};

const getTicketingAccounting = async (filters: Sortable<Paginated>) => {
  try {
    const { data } = await privateAxios.get<
      PaginatedResponse<AccountingObject>
    >(`/accounting/ticketing`, { params: filters });

    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetTicketingAccounting = (filters: Sortable<Paginated>) => {
  return useQuery({
    queryKey: accountingQueryKeys.ticketing(filters),
    queryFn: () => getTicketingAccounting(filters),
  });
};

type GetInvoicesAccountingParams = Sortable<Paginated<AccountingFilters>> & {
  isEventInvoice?: boolean;
};

const getInvoicesAccounting = async (filters: GetInvoicesAccountingParams) => {
  try {
    const { data } = await privateAxios.get<PaginatedResponse<InvoiceObject>>(
      `/accounting/invoices`,
      { params: filters }
    );

    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetInvoicesAccounting = (
  filters: GetInvoicesAccountingParams
) => {
  return useQuery({
    queryKey: accountingQueryKeys.invoices(filters),
    queryFn: () => getInvoicesAccounting(filters),
  });
};

const getPurchasesAccounting = async (
  filters: Sortable<Paginated<AccountingFilters>>
) => {
  try {
    const { data } = await privateAxios.get<PaginatedResponse<PurchaseObject>>(
      `/accounting/purchases`,
      { params: filters }
    );

    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetPurchasesAccounting = (
  filters: Sortable<Paginated<AccountingFilters>>
) => {
  return useQuery({
    queryKey: accountingQueryKeys.purchases(filters),
    queryFn: () => getPurchasesAccounting(filters),
  });
};
